import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { LooseEndBrandLockup } from "@/components/looseend/BrandLockup";
import { GoogleSignInButton } from "@/components/looseend/GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import {
  EmailAlreadyRegisteredError,
  sanitizeRedirect,
  signInWithEmail,
  signUpWithEmail,
  useAuthSession,
} from "@/lib/auth/session";
import { beginGuestHandoff } from "@/lib/auth/guest/guest.functions";
import { conversationIdFromPath, stashGuestHandoff } from "@/lib/auth/guest/guestHandoff";

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: sanitizeRedirect(search["redirect"]),
  }),
  head: () => ({
    meta: [
      { title: "Sign in to save your history — L∞seEnd™" },
      {
        name: "description",
        content:
          "Sign in to L∞seEnd™ with Google or email to save your conversations and reach them from any device. No account is needed to start.",
      },
      { property: "og:title", content: "Sign in to save your history — L∞seEnd™" },
      {
        property: "og:description",
        content: "Save and access your L∞seEnd™ conversations across devices.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AuthPage,
});

/**
 * Guest-first access: this page is OPTIONAL. A guest arrives here to save
 * their history; before any sign-in with a different identity starts, the
 * guest session is remembered (single-use, tab-scoped) so the account can
 * claim the guest's hosted conversations afterwards.
 */
function AuthPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const beginHandoff = useServerFn(beginGuestHandoff);
  const { status, user } = useAuthSession();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  const isGuest = status === "authenticated" && user?.isGuest === true;

  useEffect(() => {
    if (status === "authenticated" && user && !user.isGuest)
      void navigate({ to: redirect, replace: true });
  }, [status, user, redirect, navigate]);

  /**
   * Remembers the guest identity so its history can be claimed after sign-in.
   * The server issues a single-use ticket; no session credential is stored.
   */
  const rememberGuest = async () => {
    const { data } = await supabase.auth.getUser();
    const guest = data.user;
    if (!guest || guest.is_anonymous !== true) return;
    try {
      const issued = await beginHandoff({});
      if (!issued.ok) return;
      stashGuestHandoff({
        guestUserId: guest.id,
        ticket: issued.value.ticket,
        conversationId: conversationIdFromPath(redirect),
      });
    } catch {
      // Sign-in proceeds regardless; the guest history is simply not claimable.
    }
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPending(true);
    try {
      if (mode === "signin") {
        await rememberGuest();
        await signInWithEmail(email, password);
        void navigate({ to: redirect, replace: true });
      } else {
        const { needsConfirmation, upgraded } = await signUpWithEmail(email, password);
        if (needsConfirmation) {
          toast.success(
            upgraded
              ? "Check your email to confirm. Your conversations stay right here meanwhile."
              : "Check your email to confirm your account.",
          );
          if (upgraded) void navigate({ to: redirect, replace: true });
        } else {
          void navigate({ to: redirect, replace: true });
        }
      }
    } catch (error) {
      if (error instanceof EmailAlreadyRegisteredError) {
        setMode("signin");
        toast.info("That email already has an account. Sign in to bring your conversations along.");
        return;
      }
      toast.error(
        mode === "signin"
          ? "That email and password combination didn't work."
          : "That account couldn't be created.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="grid min-h-dvh place-items-center bg-background px-6">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-7">
        <LooseEndBrandLockup variant="full" />
        <h1 className="mt-6 text-xl font-semibold tracking-tight text-foreground">
          {isGuest ? "Save your history" : "Sign in"}
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {isGuest
            ? "Sign in to keep your conversations and reach them from any device. What you've started here comes with you."
            : "Sign in to access your saved conversations across devices."}
        </p>

        <GoogleSignInButton
          className="mt-5 w-full"
          label="Continue with Google"
          onBeforeSignIn={rememberGuest}
          {...(typeof window !== "undefined"
            ? {
                redirectTo: `${window.location.origin}/auth?redirect=${encodeURIComponent(redirect)}`,
              }
            : {})}
        />

        {showEmail ? (
          <form className="mt-4 space-y-3" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="auth-email">Email</Label>
              <Input
                id="auth-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="auth-password">Password</Label>
              <Input
                id="auth-password"
                type="password"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={pending}>
              {mode === "signin" ? "Sign in with email" : "Create account"}
            </Button>
            <button
              type="button"
              className="w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            >
              {mode === "signin" ? "Need an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </form>
        ) : (
          <Button variant="outline" className="mt-3 w-full" onClick={() => setShowEmail(true)}>
            Continue with email
          </Button>
        )}

        <Link
          to={redirect}
          className="mt-5 block text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
          data-testid="auth-continue-guest"
        >
          Continue without an account
        </Link>
      </div>
    </main>
  );
}
